import { GoldExpSystem } from './goldExpSystem.js';
import { applyHawkReveal } from './vision.js';

export class Projectile {
  constructor({ id, sourceEntity, targetEntity = null, type = 'auto', angle = 0, speed = 1500, damage = 0, maxDistance = 20.0, targetX = 0, targetZ = 0, isCrit = false, hitRadius = 0.25 }) {
    this.id = id;
    this.sourceEntity = sourceEntity;
    this.targetEntity = targetEntity;
    this.type = type; // 'auto', 'skillshot_w', 'hawkshot_e', 'ultimate_r', 'minion_proj'
    this.angle = angle;
    this.speed = speed;
    this.damage = damage;
    this.maxDistance = maxDistance;
    this.targetX = targetX;
    this.targetZ = targetZ;
    this.hitRadius = Number.isFinite(hitRadius) ? hitRadius : 0.25;

    this.x = sourceEntity ? sourceEntity.x : 0;
    this.y = sourceEntity ? sourceEntity.y + 1.2 : 0;
    this.z = sourceEntity ? sourceEntity.z : 0;

    this.distanceTraveled = 0;
    this.hasHit = false;
    this.facingAngle = angle;
    this.isCrit = isCrit;
  }

  // Actualizar Posición del Proyectil (Homing, Skillshot o Hawk)
  updatePosition(dt, potentialTargets = [], bushZones = []) {
    if (this.hasHit) return true;

    const moveStep = (this.speed / 100) * dt;

    // 1. Proyectiles Teledirigidos (Auto-Ataques y Homing Minions)
    if (this.type === 'auto' || this.type === 'minion_proj') {
      if (!this.targetEntity || this.targetEntity.isDead) {
        this.hasHit = true;
        return true;
      }

      const dx = this.targetEntity.x - this.x;
      const dz = this.targetEntity.z - this.z;
      const dist = Math.hypot(dx, dz);
      this.facingAngle = Math.atan2(dx, dz);

      if (dist <= moveStep) {
        this.x = this.targetEntity.x;
        this.z = this.targetEntity.z;
        this.hasHit = true;

        const hitRes = this.targetEntity.takeDamage(this.damage, this.sourceEntity, 'physical', this.isCrit);
        this.lastHitEntity = this.targetEntity;
        if (hitRes.killed && this.sourceEntity && !this.targetEntity.bountyPaid) {
          this.targetEntity.bountyPaid = true;
          const gold = GoldExpSystem.awardKillBounty(this.sourceEntity, this.targetEntity);
          if (this.sourceEntity.onKillCallback) {
            this.sourceEntity.onKillCallback(this.targetEntity, gold);
          }
        }
        return true;
      } else {
        this.x += (dx / dist) * moveStep;
        this.z += (dz / dist) * moveStep;
        return false;
      }
    }

    // 2. Disparo de Halcón (E Hawkshot)
    if (this.type === 'hawkshot_e') {
      const dx = this.targetX - this.x;
      const dz = this.targetZ - this.z;
      const dist = Math.hypot(dx, dz);
      this.facingAngle = Math.atan2(dx, dz);

      let arrived = false;
      if (dist <= moveStep) {
        this.x = this.targetX;
        this.z = this.targetZ;
        this.hasHit = true;
        arrived = true;
      } else {
        this.x += (dx / dist) * moveStep;
        this.z += (dz / dist) * moveStep;
      }

      applyHawkReveal(potentialTargets, this.x, this.z, this.sourceEntity.team);

      return arrived;
    }

    // 3. Skillshots (W Volley y R Ultimate)
    const vx = Math.sin(this.angle) * moveStep;
    const vz = Math.cos(this.angle) * moveStep;

    this.x += vx;
    this.z += vz;
    this.distanceTraveled += Math.hypot(vx, vz);
    this.facingAngle = this.angle;

    // Verificación de Impacto contra Enemigos
    const hitEnemy = potentialTargets.find(t => {
      if (!t || t.isDead || t === this.sourceEntity || t.team === this.sourceEntity.team) return false;
      const dx = t.x - this.x;
      const dz = t.z - this.z;
      const dist = Math.hypot(dx, dz);
      const projRadius = this.hitRadius || 0.25;
      const targetRadius = (t.radius / 16);
      return dist <= (projRadius + targetRadius);
    });

    if (hitEnemy) {
      this.hasHit = true;

      if (this.type === 'ultimate_r') {
        const distFromSource = Math.hypot(this.x - this.sourceEntity.x, this.z - this.sourceEntity.z);
        const stunDuration = Math.min(3.5, Math.max(1.0, 1.0 + (distFromSource / 12.0) * 2.5));
        hitEnemy.applyStun(stunDuration);
      }

      const hitRes = hitEnemy.takeDamage(this.damage, this.sourceEntity, 'physical', this.isCrit);
      this.lastHitEntity = hitEnemy;
      if (hitRes.killed && this.sourceEntity && !hitEnemy.bountyPaid) {
        hitEnemy.bountyPaid = true;
        const gold = GoldExpSystem.awardKillBounty(this.sourceEntity, hitEnemy);
        if (this.sourceEntity.onKillCallback) {
          this.sourceEntity.onKillCallback(hitEnemy, gold);
        }
      }
      return true;
    }

    if (this.distanceTraveled >= this.maxDistance) {
      this.hasHit = true;
      return true;
    }

    return false;
  }
}
